package org.example.project.firebase

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.delay
import org.example.project.model.AttackType
import org.example.project.model.MultiplayerGame
import org.example.project.model.QueuePlayer
import org.example.project.model.SerializedShip
import org.example.project.model.User
import org.example.project.game.AttackEngine
import org.example.project.game.ScoringEngine
import org.example.project.util.currentTimeMillis

object MatchmakingManager {

    private val db = Firebase.firestore
    private const val QUEUE = "matchmaking"
    private const val GAMES = "games"

    suspend fun joinQueue(user: User) {
        val uid = AuthManager.waitForUserId()
        if (uid == null || uid.isEmpty()) {
            println("ERROR: Cannot join queue - user not authenticated or UID is empty")
            throw IllegalStateException("Not signed in")
        }

        if (user.uid.isEmpty()) {
            println("ERROR: Cannot join queue - user UID is empty")
            throw IllegalStateException("Invalid user UID")
        }

        db.collection(QUEUE)
            .document(user.uid)
            .set(
                mapOf(
                    "uid" to user.uid,
                    "username" to user.username,
                    "rating" to user.rating,
                    "joinedAt" to currentTimeMillis().toDouble()
                )
            )

        FirestoreManager.setInGame(user.uid, true)
        println("Joined queue: ${user.username}")
    }

    suspend fun leaveQueue() {
        val uid = AuthManager.currentUserId()
        if (uid == null || uid.isEmpty()) return
        removeFromQueue(uid)
        FirestoreManager.setInGame(uid, false)
        db.collection("users")
            .document(uid)
            .set(
                mapOf(
                    "currentGameId" to ""
                ),
                merge = true
            )
    }


    private suspend fun removeFromQueue(uid: String) {
        if (uid.isEmpty()) return
        db.collection(QUEUE).document(uid).delete()
    }

    private suspend fun markPlayersMatched(gameId: String,p1Uid: String, p2Uid: String) {
        removeFromQueue(p1Uid)
        removeFromQueue(p2Uid)
        FirestoreManager.setInGame(p1Uid, true)
        FirestoreManager.setInGame(p2Uid, true)
        db.collection("users")
            .document(p1Uid)
            .set(
                mapOf("currentGameId" to gameId),
                merge = true
            )

        db.collection("users")
            .document(p2Uid)
            .set(
                mapOf("currentGameId" to gameId),
                merge = true
            )
    }

//    private suspend fun isInActiveGame(uid: String): Boolean {
//        val gamesSnapshot = db.collection(GAMES).get()
//        return gamesSnapshot.documents.any { doc ->
//            val game = runCatching { doc.data<MultiplayerGame>() }.getOrNull() ?: return@any false
//            game.status != "FINISHED" &&
//                (game.player1Uid == uid || game.player2Uid == uid)
//        }
//    }

    suspend fun findMatch(user: User): String? {
        AuthManager.waitForUserId() ?: return null

        joinQueue(user)
        println("Searching...")

        repeat(60) {
            val gameId = tryCreateMatch(user.uid)
            println("tryCreateMatch = $gameId")
            if (gameId != null) return gameId
            println("Loop $it")
            val existingId = findExistingGame(user.uid)
            println("existingGame = $existingId")
            if (existingId != null) {
                removeFromQueue(user.uid)
                FirestoreManager.setInGame(user.uid, true)
                return existingId
            }

            delay(1500)
        }

        leaveQueue()
        return null
    }

    private suspend fun tryCreateMatch(myUid: String): String? {
        val snapshot = db.collection(QUEUE).get()
        val players = snapshot.documents
            .mapNotNull {
                runCatching {
                    it.data<QueuePlayer>()
                }.getOrNull()
            }
            .sortedBy { it.joinedAt }
        println("Players in queue:")
        players.forEach {
            println("${it.username} ${it.uid}")
        }
        println("Queue size = ${players.size}")



        if (players.size < 2) return null

        val p1 = players[0]
        val p2 = players[1]

        println("Me = $myUid")
        println("P1 = ${p1.uid}")
        println("P2 = ${p2.uid}")
        println("Min = ${minOf(p1.uid, p2.uid)}")

        if (myUid != minOf(p1.uid, p2.uid)) return null

        val gameId = gameIdFor(p1.uid, p2.uid)
        val existing = db.collection(GAMES).document(gameId).get()

        if (existing.exists) {
            val status = runCatching {
                existing.data<MultiplayerGame>().status
            }.getOrNull()

            if (status != null && status != "FINISHED") {
                markPlayersMatched(gameId,p1.uid, p2.uid)
                return gameId
            }
        }

        val gameData = buildGameDocument(
            gameId = gameId,
            p1Uid = p1.uid,
            p2Uid = p2.uid,
            p1Name = p1.username,
            p2Name = p2.username
        )
        println("Creating game $gameId")

        db.collection(GAMES).document(gameId).set(gameData)
        markPlayersMatched(gameId,p1.uid, p2.uid)
        println("Game created")

        return gameId
    }

    private suspend fun findExistingGame(myUid: String): String? {
        val userDoc =
            db.collection("users")
                .document(myUid)
                .get()

        val currentGameId =
            userDoc.get("currentGameId") as? String

        if (!currentGameId.isNullOrEmpty()) {

            val gameDoc =
                db.collection(GAMES)
                    .document(currentGameId)
                    .get()

            if (gameDoc.exists) {
                val status =
                    runCatching {
                        gameDoc.data<MultiplayerGame>().status
                    }.getOrNull()

                if (status != null && status != "FINISHED")
                    return currentGameId
            }
        }

        return null
    }

    fun gameIdFor(uid1: String, uid2: String): String =
        listOf(uid1, uid2).sorted().joinToString("_")

    private fun buildGameDocument(
        gameId: String,
        p1Uid: String,
        p2Uid: String,
        p1Name: String,
        p2Name: String
    ): Map<String, Any> = mapOf(
        "gameId" to gameId,
        "player1Uid" to p1Uid,
        "player2Uid" to p2Uid,
        "player1Name" to p1Name,
        "player2Name" to p2Name,
        "status" to "SHIP_PLACEMENT",
        "currentTurnUid" to p1Uid,
        "winnerUid" to "",
        "player1Ready" to false,
        "player2Ready" to false,
        "player1Ships" to emptyList<SerializedShip>(),
        "player2Ships" to emptyList<SerializedShip>(),
        "attacksOnPlayer1" to List(100) { "UNKNOWN" },
        "attacksOnPlayer2" to List(100) { "UNKNOWN" },
        "player1Score" to 0,
        "player2Score" to 0,
        "player1Penalties" to 0,
        "player2Penalties" to 0,
        "player1Correct" to 0,
        "player2Correct" to 0,
        "player1Charges" to mapOf(
            "crossFire" to 2,
            "clusterBomb" to 1,
            "lineStrike" to 1,
            "sonar" to 2
        ),
        "player2Charges" to mapOf(
            "crossFire" to 2,
            "clusterBomb" to 1,
            "lineStrike" to 1,
            "sonar" to 2
        ),
        "lastEvent" to "",
        "countdownStart" to 0.0,
        "turnStartTime" to currentTimeMillis(),
        "placementStartTime" to currentTimeMillis(),
    )
}
